/**
 * Email Lists Management Page
 * Manage subscriber lists for email broadcast
 *
 * Created: January 3, 2026
 */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Card, Badge } from "react-bootstrap";
import Modal from "react-modal";
import { toast } from "react-toastify";

import { selectUser } from "../../../components/auth/auth.selector";
import HelmetWrapper from "../../../HelmetWrapper";
import CommonStructured from "../../../components/common-components/CommonStructured";
import CmnFilters from "../../../components/lead-management/common/CmnFilters";
import ModalHeadAndClose from "../../../components/payouts-wtf/common/ModalHeadAndClose";
import ReactVortoxSpinner from "../../../components/common-components/ReactVortoxSpinner";
import PaymentLinkTable from "../../../components/payment-link-system/PaymentLinkTable";

import { fetchEmailListsData } from "../../../redux/email-broadcast/emailBroadcastSlice";
import {
  createEmailList,
  updateEmailList,
  deleteEmailList,
} from "../../../redux/email-broadcast/emailBroadcastApi";
import { formatDateTimezon } from "../../../components/common-data/Converts";
import EmailListFormEnhanced from "../../../components/email-broadcast/EmailListFormEnhanced";
import CsvImportModal from "../../../components/email-broadcast/CsvImportModal";

// Simple Edit Form Component (for quick edits)
const SimpleEditForm = ({
  rowData,
  closeModal,
  userInfoData,
  callFunction,
}) => {
  const [formData, setFormData] = useState({
    name: rowData?.name || "",
    description: rowData?.description || "",
    opt_in_type: rowData?.opt_in_type || "single",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("List name is required");
      return;
    }

    setLoading(true);
    try {
      const response = await updateEmailList(
        rowData.id || rowData.uid,
        formData,
        userInfoData?.token
      );

      if (response?.status) {
        toast.success(response.message || "List updated successfully");
        callFunction();
        closeModal();
      } else {
        toast.error(response?.message || "Operation failed");
      }
    } catch (error) {
      toast.error(error.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-3">
      <div className="mb-3">
        <label className="form-label">List Name *</label>
        <input
          type="text"
          className="form-control"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., All Members 2026"
          required
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Description</label>
        <textarea
          className="form-control"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          placeholder="Brief description of this list"
          rows={3}
        />
      </div>

      <div className="mb-4">
        <label className="form-label">Opt-in Type</label>
        <select
          className="form-select"
          value={formData.opt_in_type}
          onChange={(e) =>
            setFormData({ ...formData, opt_in_type: e.target.value })
          }
        >
          <option value="single">Single Opt-in (Immediate)</option>
          <option value="double">Double Opt-in (Confirmation Required)</option>
        </select>
        <small className="text-muted">
          Single opt-in adds subscribers immediately. Double opt-in requires
          email confirmation.
        </small>
      </div>

      <div className="d-flex gap-2">
        <button
          type="submit"
          className="btn btn-primary flex-grow-1"
          disabled={loading}
        >
          {loading ? "Saving..." : "Update List"}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={closeModal}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const EmailLists = () => {
  const dispatch = useDispatch();
  const userInfoData = useSelector(selectUser);
  const { emailLists, emailListsLoading } = useSelector(
    (state) => state.emailBroadcast || {}
  );

  // Debug: Log the data
  console.log("📧 Email Lists Data:", emailLists);
  console.log("📊 Email Lists Data Array:", emailLists?.data);

  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);

  const iniAllFilter = { searchString: "" };
  const [allFilters, setAllFilters] = useState(iniAllFilter);
  const [filters, setFilters] = useState({ page: 1, limit: 10 });
  const [searchFilterApplied, setSearchFilterApplied] = useState(false);

  const iniModValues = { isModOpen: false, modName: "", rowData: "" };
  const [modValues, setModValues] = useState(iniModValues);
  const [csvImportListId, setCsvImportListId] = useState(null);

  const fetchLists = (filters) => {
    dispatch(fetchEmailListsData({ filters, token: userInfoData?.token }));
  };

  useEffect(() => {
    fetchLists(filters);
  }, [filters]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, page: isPage + 1, limit: pageLimit }));
  }, [isPage, pageLimit]);

  useEffect(() => {
    if (allFilters.searchString?.length === 0) {
      setSearchFilterApplied(false);
      setFilters((prev) => ({ ...prev, search: "" }));
      setIsPage(0);
    }
  }, [allFilters.searchString]);

  const onClickSearch = () => {
    setIsPage(0);
    if (allFilters.searchString) {
      setSearchFilterApplied(true);
      setFilters((prev) => ({ ...prev, search: allFilters.searchString }));
    }
  };

  const onResetFilters = () => {
    setAllFilters(iniAllFilter);
    setFilters({ page: 1, limit: 10 });
  };

  const handleDelete = async (row) => {
    if (window.confirm(`Are you sure you want to delete "${row.name}"?`)) {
      try {
        const response = await deleteEmailList(
          row.id || row.uid,
          userInfoData?.token
        );
        if (response?.status) {
          toast.success("List deleted successfully");
          fetchLists(filters);
        } else {
          toast.error(response?.message || "Failed to delete");
        }
      } catch (error) {
        toast.error("Failed to delete list");
      }
    }
  };

  const tbColumns = [
    {
      dataField: "name",
      text: "List Name",
      formatter: (_, row) => (
        <div>
          <span className="fw-medium">{row.name}</span>
          {row.description && (
            <small className="d-block text-muted">
              {row.description.substring(0, 50)}...
            </small>
          )}
        </div>
      ),
    },
    {
      dataField: "subscriber_count",
      text: "Subscribers",
      formatter: (_, row) => (
        <div>
          <span className="fw-bold">
            {(row.subscriber_count || 0).toLocaleString()}
          </span>
          <small className="d-block text-muted">
            {row.active_count || 0} active
          </small>
        </div>
      ),
    },
    {
      dataField: "opt_in_type",
      text: "Opt-in",
      formatter: (_, row) => (
        <Badge bg={row.opt_in_type === "double" ? "info" : "secondary"}>
          {row.opt_in_type || "single"}
        </Badge>
      ),
    },
    {
      dataField: "date_added",
      text: "Created",
      formatter: (_, row) => (
        <small>{formatDateTimezon(row.date_added) || "N/A"}</small>
      ),
    },
    {
      dataField: "action",
      text: "Actions",
      formatter: (_, row) => (
        <div className="d-flex gap-2 flex-wrap">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() =>
              setModValues({ isModOpen: true, modName: "edit", rowData: row })
            }
          >
            Edit
          </button>
          <button
            className="btn btn-sm btn-outline-success"
            onClick={() => setCsvImportListId(row.uid || row.id)}
            title="Import subscribers from CSV"
          >
            Import CSV
          </button>
          <button
            className="btn btn-sm btn-outline-info"
            onClick={() =>
              (window.location.href = `/broadcast-system/email-broadcast/lists/${row.uid}/subscribers`)
            }
          >
            Subscribers
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <HelmetWrapper title="WTF: Email Lists">
      <Container className="bg-cloud-k px-50px gobal-container" fluid>
        <Modal
          isOpen={modValues.isModOpen}
          onRequestClose={() => setModValues(iniModValues)}
          contentLabel="List Modal"
          ariaHideApp={false}
          className={modValues.modName === "add" ? "modal-xl" : ""}
        >
          <div
            className={`createNewCTOModalContainer ${
              modValues.modName === "add" ? "mod-width-80p" : "mod-width-50p"
            }`}
          >
            <ModalHeadAndClose
              title={
                modValues.modName === "edit" ? "Edit List" : "Create New List"
              }
              onClose={() => setModValues(iniModValues)}
            />
            {modValues.modName === "edit" ? (
              <SimpleEditForm
                rowData={modValues.rowData}
                closeModal={() => setModValues(iniModValues)}
                userInfoData={userInfoData}
                callFunction={() => fetchLists(filters)}
              />
            ) : (
              <EmailListFormEnhanced
                isEdit={false}
                rowData={null}
                closeModal={() => setModValues(iniModValues)}
                userInfoData={userInfoData}
                callFunction={() => fetchLists(filters)}
              />
            )}
          </div>
        </Modal>

        <CsvImportModal
          isOpen={!!csvImportListId}
          onClose={() => setCsvImportListId(null)}
          listId={csvImportListId}
          userInfoData={userInfoData}
          onSuccess={() => {
            fetchLists(filters);
            setCsvImportListId(null);
          }}
        />

        <Row>
          <Col>
            <CommonStructured
              pageHeading="Email Lists"
              isReload={() => fetchLists(filters)}
              createBtnTxt="+ Create New List"
              onClickCreateBtn={() =>
                setModValues({ isModOpen: true, modName: "add", rowData: "" })
              }
            />
          </Col>
        </Row>

        {/* <Row>
          <Col>
            <CmnFilters
              from="email-lists"
              userInfoData={userInfoData}
              searchFilterApplied={searchFilterApplied}
              filters={allFilters}
              setFilters={setAllFilters}
              onClickSearch={onClickSearch}
              onClickReset={onResetFilters}
              isLoading={emailListsLoading}
              searchPlaceHolder="Search by list name..."
            />
          </Col>
        </Row> */}

        <Row>
          <Col>
            <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mt15">
              {emailListsLoading ? (
                <ReactVortoxSpinner />
              ) : !emailLists?.data || emailLists.data.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">
                    No email lists found. Create your first list to get started!
                  </p>
                </div>
              ) : (
                <PaymentLinkTable
                  tableData={emailLists?.data}
                  tableColumns={tbColumns}
                  tablePagination={emailLists?.pagination}
                  isPage={isPage}
                  setIsPage={setIsPage}
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                />
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default EmailLists;
